import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { SmartCTA } from '../components/SmartCTAs';
import marketingTheme from '../themes/marketingTheme';

const HeroSection = styled(Box)(({ theme }) => ({
  padding: '100px 0 60px',
  textAlign: 'center',
  backgroundColor: 'white',
  borderBottom: '1px solid #E5E7EB',
}));

const StepCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '40px',
  marginBottom: '64px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
  border: '1px solid #E5E7EB',
  '& .MuiCardContent-root': {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
}));

const CTASection = styled(Box)(({ theme }) => ({
  padding: '100px 0',
  textAlign: 'center',
  background: `linear-gradient(135deg, ${marketingTheme.palette.primary.main} 0%, ${marketingTheme.palette.primary.dark} 100%)`,
  color: '#000',
}));

const StepImage = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '400px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  overflow: 'hidden',
  '& img': {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    objectPosition: 'center',
  },
}));

const HowItWorks = () => {
  return (
    <Box sx={{ pt: 10, backgroundColor: '#F9FAFB' }}>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '36px', md: '48px' },
              fontWeight: 700,
              mb: 2,
              color: '#1F2937',
              lineHeight: 1.2,
            }}
          >
            Transform industrial processes{' '}
            <Box component="span" sx={{ display: 'block', color: marketingTheme.palette.primary.main }}>
              with spatial computing
            </Box>
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: '18px',
              fontWeight: 400,
              maxWidth: '700px',
              margin: '0 auto 32px',
              color: '#6B7280',
              lineHeight: 1.5,
            }}
          >
            Surface production problems instantly. Give managers the full picture. 
            Improve productivity and safety with comprehensive process flow analysis.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <SmartCTA
              context="experience-demo"
              fallbackHref="/contact"
              fallbackText="Request demo"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: marketingTheme.palette.primary.main,
                color: '#000',
                '&:hover': { backgroundColor: marketingTheme.palette.primary.dark },
                px: 4,
                py: 1.5,
              }}
            />
            <SmartCTA
              context="implementation-planning"
              fallbackHref="/contact"
              fallbackText="Learn more"
              variant="outlined"
              size="large"
              sx={{
                color: '#1F2937',
                borderColor: '#D1D5DB',
                '&:hover': { backgroundColor: '#F3F4F6' },
                px: 4,
                py: 1.5,
              }}
            />
          </Box>
        </Container>
      </HeroSection>

      {/* Steps Section */}
      <Box sx={{ py: 10, backgroundColor: 'white' }}>
        <Container maxWidth="xl">
          {/* Step 1 */}
          <StepCard>
            <CardContent>
              <Grid container spacing={6} alignItems="center">
                <Grid item xs={12} md={5}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Chip
                      label="STEP 1"
                      sx={{
                        backgroundColor: 'transparent',
                        color: marketingTheme.palette.primary.main,
                        fontWeight: 600,
                        fontSize: '15px',
                        letterSpacing: '1px',
                        alignSelf: 'flex-start',
                      }}
                    />
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#1F2937',
                        pb: 2,
                        borderBottom: '1px solid #E5E7EB',
                      }}
                    >
                      Deploy cameras in your facility
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#6B7280',
                        pb: 2.5,
                        borderBottom: '1px solid #E5E7EB',
                        lineHeight: 1.6,
                      }}
                    >
                      Install our computer vision cameras at key locations throughout your industrial workspace. 
                      Our hardware captures 3D video data of worker ergonomic movements and process flows.
                    </Typography>
                    {/* <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#6B7280',
                        lineHeight: 1.6,
                      }}
                    >
                      Privacy-first design ensures worker anonymity while collecting valuable process data.
                    </Typography> */}
                  </Box>
                </Grid>
                <Grid item xs={12} md={7}>
                  <StepImage>
                    <img
                      src="/images/camera_in_factory.png"
                      alt="Camera deployment in industrial facility"
                    />
                  </StepImage>
                </Grid>
              </Grid>
            </CardContent>
          </StepCard>

          {/* Step 2 */}
          <StepCard>
            <CardContent>
              <Grid container spacing={6} alignItems="center">
                <Grid item xs={12} md={7} sx={{ order: { xs: 2, md: 1 } }}>
                  <StepImage>
                    <img
                      src="/images/spaghetti_plot.png"
                      alt="AI analysis of worker movements"
                    />
                  </StepImage>
                </Grid>
                <Grid item xs={12} md={5} sx={{ order: { xs: 1, md: 2 } }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Chip
                      label="STEP 2"
                      sx={{
                        backgroundColor: 'transparent',
                        color: marketingTheme.palette.primary.main,
                        fontWeight: 600,
                        fontSize: '15px',
                        letterSpacing: '1px',
                        alignSelf: 'flex-start',
                      }}
                    />
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#1F2937',
                        pb: 2,
                        borderBottom: '1px solid #E5E7EB',
                      }}
                    >
                      AI analyzes movement patterns
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#6B7280',
                        pb: 2.5,
                        borderBottom: '1px solid #E5E7EB',
                        lineHeight: 1.6,
                      }}
                    >
                      Our machine learning algorithms process video data to identify worker movements, 
                      detect safety violations, and measure time-and-motion metrics automatically.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#6B7280',
                        lineHeight: 1.6,
                      }}
                    >
                      Real-time analysis provides immediate insights into process efficiency and safety risks.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </StepCard>

          {/* Step 3 */}
          <StepCard>
            <CardContent>
              <Grid container spacing={6} alignItems="center">
                <Grid item xs={12} md={5}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Chip
                      label="STEP 3"
                      sx={{
                        backgroundColor: 'transparent',
                        color: marketingTheme.palette.primary.main,
                        fontWeight: 600,
                        fontSize: '15px',
                        letterSpacing: '1px',
                        alignSelf: 'flex-start',
                      }}
                    />
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#1F2937',
                        pb: 2,
                        borderBottom: '1px solid #E5E7EB',
                      }}
                    >
                      Receive actionable insights
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#6B7280',
                        pb: 2.5,
                        borderBottom: '1px solid #E5E7EB',
                        lineHeight: 1.6,
                      }}
                    >
                      Get comprehensive reports and dashboards showing productivity metrics, safety incidents, 
                      and optimization opportunities. Our platform delivers clear, actionable data.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#6B7280',
                        lineHeight: 1.6,
                      }}
                    >
                      Continuous monitoring enables data-driven decision making and process improvements.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={7}>
                  <StepImage>
                    <img
                      src="/images/primary_dashboard.png"
                      alt="Analytics dashboard showing insights"
                    />
                  </StepImage>
                </Grid>
              </Grid>
            </CardContent>
          </StepCard>
        </Container>
      </Box>

      {/* Value Proposition Section */}
      <Box sx={{ py: 10, backgroundColor: '#F9FAFB' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: '12px',
              p: 5,
              textAlign: 'center',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
              border: '1px solid #E5E7EB',
            }}
          >
            <Typography
              sx={{
                color: '#6B7280',
                mb: 4,
                fontSize: '18px',
              }}
            >
              MotionLogic provides clear comprehension of process flow to improve productivity and safety
            </Typography>
            <Grid container spacing={3} sx={{ maxWidth: '900px', mx: 'auto', mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    backgroundColor: '#F9FAFB',
                    p: 3,
                    borderRadius: '8px',
                    textAlign: 'left',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: '18px',
                      mb: 2,
                      color: '#1F2937',
                    }}
                  >
                    Key Metrics Tracked
                  </Typography>
                  <Box component="ul" sx={{ pl: 2.5, color: '#6B7280' }}>
                    <li>Worker ergonomic movement patterns and efficiency</li>
                    <li>Time-and-motion analysis</li>
                    <li>Safety compliance monitoring</li>
                    <li>Ergonomic risk assessment</li>
                    <li>Process bottleneck identification</li>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    backgroundColor: '#F9FAFB',
                    p: 3,
                    borderRadius: '8px',
                    textAlign: 'left',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: '18px',
                      mb: 2,
                      color: '#1F2937',
                    }}
                  >
                    Business Benefits
                  </Typography>
                  <Box component="ul" sx={{ pl: 2.5, color: '#6B7280' }}>
                    <li>15-30% improvement in productivity</li>
                    <li>Reduced workplace injuries</li>
                    <li>Enhanced compliance with safety standards</li>
                    <li>Data-driven process optimization</li>
                    <li>Faster identification of process inefficiencies</li>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <CTASection>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: '36px',
              fontWeight: 700,
              mb: 4,
              color: 'white',
            }}
          >
            Ready to transform your operations?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <SmartCTA
              context="experience-demo"
              fallbackHref="/contact"
              fallbackText="Request a demo"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: 'white',
                color: marketingTheme.palette.primary.main,
                '&:hover': { backgroundColor: '#F3F4F6' },
                px: 4,
                py: 1.5,
              }}
            />
            <SmartCTA
              context="implementation-planning"
              fallbackHref="/contact"
              fallbackText="Learn more"
              variant="outlined"
              size="large"
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'white',
                },
                px: 4,
                py: 1.5,
              }}
            />
          </Box>
        </Container>
      </CTASection>
    </Box>
  );
};

export default HowItWorks;
